import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// ── In-memory token store (NOT localStorage — XSS safe) ─────────────────────
// On page reload it's cleared; httpOnly cookie handles persistence. We keep
// it for the few code paths (POS, mobile, websockets) that read the token via
// `setInMemoryToken` after login.
let _inMemoryToken = null;
let _hasActiveSession = false;

export function setInMemoryToken(token) { _inMemoryToken = token; }
export function clearInMemoryToken() { _inMemoryToken = null; }
export function setActiveSession(val) { _hasActiveSession = val; }

// ── Axios instances ─────────────────────────────────────────────────────────
// `api` is the public instance with the full refresh-on-401 interceptor.
// `rawApi` is a no-interceptor instance used internally for the refresh call
// itself, to avoid recursive interceptor loops.
const api = axios.create({ baseURL: API_BASE, withCredentials: true });
const rawApi = axios.create({ baseURL: API_BASE, withCredentials: true });

api.interceptors.request.use((config) => {
  if (_inMemoryToken) {
    config.headers.Authorization = `Bearer ${_inMemoryToken}`;
  }
  return config;
});

// ── Single-flight refresh ───────────────────────────────────────────────────
// Multiple concurrent 401s share ONE in-flight `/auth/refresh` call. Without
// this, every failed request would spawn its own refresh and the responses
// race against each other (the last winning Set-Cookie may overwrite the
// fresh one with a stale one — root cause of admin auto-logout under load).
let _refreshPromise = null;

function refreshSession() {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = rawApi.post("/auth/refresh")
    .then((res) => {
      if (res.data?.token) {
        _inMemoryToken = res.data.token;
      }
      return res.data;
    })
    .finally(() => { _refreshPromise = null; });
  return _refreshPromise;
}

// Login/refresh endpoints — a 401 from these is the ONLY legitimate logout.
// Anywhere else, a 401 just means "this access token is dead, refresh it".
const HARD_AUTH_ENDPOINTS = [
  "/auth/refresh",
  "/auth/admin/login",
  "/auth/admin/email-otp/verify",
  "/auth/admin/otp/verify",
  "/auth/admin/google/session",
  "/auth/otp/verify",
  "/auth/otp/email-verify",
  "/auth/google/session",
  "/auth/rider/login",
];

function isHardAuthEndpoint(url) {
  return HARD_AUTH_ENDPOINTS.some((p) => url && url.includes(p));
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err.response?.status;
    const cfg = err.config || {};
    const url = cfg.url || "";

    // 1. Logout from hard-auth endpoints (login/refresh) — user explicitly
    //    cannot authenticate. Only signal we trust for auto-logout.
    if (status === 401 && isHardAuthEndpoint(url)) {
      if (_hasActiveSession) {
        _hasActiveSession = false;
        _inMemoryToken = null;
        window.dispatchEvent(new Event("nc_logout"));
      }
      return Promise.reject(err);
    }

    // 2. 401 from any other endpoint while session is active → try refresh
    //    (single-flight) and replay the original request once. This is the
    //    industry-standard pattern (Stripe / Linear / Shopify).
    if (status === 401 && _hasActiveSession && !cfg.__retried) {
      cfg.__retried = true;
      try {
        await refreshSession();
        // Refresh OK — replay the original request with fresh creds.
        return api.request(cfg);
      } catch (refreshErr) {
        // Refresh failed. The hard-auth branch above will already have fired
        // nc_logout if refresh returned 401. If it failed transiently (5xx,
        // network), we DELIBERATELY do not log the user out — they can keep
        // working and the next click will re-trigger refresh. This is the
        // single biggest source of "admin auto-logged-out for no reason"
        // bugs and the fix here is conservative on purpose.
        return Promise.reject(err);
      }
    }

    return Promise.reject(err);
  }
);

export default api;
export { API_BASE, BACKEND_URL, refreshSession };
